import { validate as validateEmail } from "email-validator";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";

export interface PhoneNumberGuess {
	guessedCountryCode: string;
	formatted: string;
}

export const unknownCountryCode = "unknown";

function cleanPhoneNumber(input: string): string {
	const onlyDigitsAndPlus = input.replace(/[^\d+]/g, "");

	// Keep only the first + if it exists, and remove others
	if (onlyDigitsAndPlus.startsWith("+")) {
		return `+${onlyDigitsAndPlus.slice(1).replace(/\+/g, "")}`;
	}
	return onlyDigitsAndPlus.replace(/\+/g, "");
}

/**
 * Supporting USA and Israel phone numbers only right now.
 * @param input
 * @param countriesToInclude - list of countries to consider when guessing (currently unused)
 */
export function guessAndFormatPhoneNumber(input: string, countriesToInclude: ("usa" | "il")[] = ["usa"]): PhoneNumberGuess {
	if (input.startsWith("+")) {
		// If input starts with +, assume it's already in international format
		const phoneNumber = parsePhoneNumberFromString(input);
		if (phoneNumber) {
			return {
				guessedCountryCode: phoneNumber.countryCallingCode,
				formatted: cleanPhoneNumber(phoneNumber.format("E.164")),
			};
		}
		return {
			guessedCountryCode: unknownCountryCode,
			formatted: cleanPhoneNumber(input),
		};
	}
	const cleaned = input.replace(/[^\d]/g, "");

	if (countriesToInclude.includes("usa")) {
		// Try US (10 digits, usually starting with 2-9)
		if (/^[2-9]\d{9}$/.test(cleaned)) {
			return {
				guessedCountryCode: "+1",
				formatted: cleanPhoneNumber(new AsYouType("US").input(`+1${cleaned}`)),
			};
		}
	}

	if (countriesToInclude.includes("il")) {
		// Try Israeli mobile numbers (10 or 9digits starting with 05 or 5)
		if (/^05\d{8}$/.test(cleaned) || /^5\d{8}$/.test(cleaned)) {
			const phoneNumber = parsePhoneNumberFromString(cleaned, "IL");
			if (phoneNumber && phoneNumber.isValid()) {
				return {
					guessedCountryCode: "+972",
					formatted: cleanPhoneNumber(phoneNumber.format("E.164")),
				};
			}
		}
	}

	// Unknown — fallback to original input
	return {
		guessedCountryCode: unknownCountryCode,
		formatted: cleanPhoneNumber(input),
	};
}

export interface PhoneNumberValidationResult {
	isValid: boolean;
	reason?: string;
	country?: string;
	e164?: string; // e.g. "+972501234567"
	national?: string; // e.g. "050-123-4567"
	international?: string; // e.g. "+972 50-123-4567"
}

export function validatePhoneNumber(input: string): PhoneNumberValidationResult {
	const phoneNumber = parsePhoneNumberFromString(input);

	if (!phoneNumber) {
		return { isValid: false, reason: "Not a valid phone number format" };
	}

	return {
		isValid: phoneNumber.isValid(),
		country: phoneNumber.country,
		e164: phoneNumber.number, // e.g. "+972501234567"
		national: phoneNumber.formatNational(), // e.g. "050-123-4567"
		international: phoneNumber.formatInternational(), // e.g. "+972 50-123-4567"
	};
}

export function isValidEmail(email: string): boolean {
	return validateEmail(email);
}

export function cleanEmail(email: string): string {
	return email.replaceAll(" ", "").toLowerCase();
}

/**
 * Formats username input based on its type (email, phone, or plain text).
 * - If it contains @, treats it as email and lowercases/trims
 * - If it's only letters/spaces, treats it as plain username
 * - If it looks like an email pattern (has letters and email-like characters), treats as email-in-progress
 * - Otherwise, formats it as a phone number
 */
/** What a half-typed sign-in username currently looks like. `unknown` = not yet decidable. */
export type UsernameInputKind = "email" | "phone" | "unknown";

/**
 * Classifies a sign-in username *as it is being typed*, for affordances that react to input.
 *
 * Deliberately shape-based rather than validity-based: it has to commit on the first character or
 * two, long before the value would pass `isValidEmail` / `validatePhoneNumber`. Lives here so the
 * rule sits beside `formatUsernameInput`, which makes the same email-vs-phone call internally.
 */
export function classifyUsernameInput(value: string): UsernameInputKind {
	const trimmed = value.trim();
	if (!trimmed) return "unknown";
	// "@" is decisive, and it is also what formatUsernameInput keys on.
	if (trimmed.includes("@")) return "email";
	// Phone-SHAPED, not merely phone-STARTING. Keying off the first character alone labelled `1abc`
	// and `+abc` as phone numbers while formatUsernameInput treats them as an email in progress and
	// the backend as a username — so the icon contradicted the identifier actually being submitted.
	if (/\d/.test(trimmed) && /^\+?[\d\s().-]+$/.test(trimmed)) return "phone";
	return "unknown";
}

export function formatUsernameInput(value: string): string {
	// If it contains @, it's an email - just lowercase and trim
	if (value.includes("@")) {
		return value.toLowerCase().trim();
	}

	// If it's only letters (and spaces), allow it as a plain username
	if (/^[a-zA-Z\s]*$/.test(value)) {
		return value;
	}

	// If it looks like an email pattern (has letters and email-like characters like +, ., _, -)
	// but no @ yet, treat it as email-in-progress and don't format
	if (/^[a-zA-Z0-9._+-]+$/.test(value) && /[a-zA-Z]/.test(value)) {
		return value.toLowerCase();
	}

	// Otherwise, try to format it as a phone number, but only if country code can be guessed
	const guessedPhoneNumber = guessAndFormatPhoneNumber(value.trim());
	if (guessedPhoneNumber.guessedCountryCode !== unknownCountryCode) {
		return guessedPhoneNumber.formatted;
	}
	return value.toLowerCase().trim();
}
